import propTypes from 'prop-types';
import { ClipLoader } from 'react-spinners';
import theme from '@/common/theme';
import Styled from './button-style';

/**
 * ETTX Primary Button component
 *
 * @param {object} props - The props object of PrimaryButton.
 * @param {Function} props.onClick - The onClick event handler (isRequired)
 * @param {string} props.title - The title of the button (isRequired)
 * @param {boolean} props.disabled - The disabled state of the button.
 * @param {object} props.style - Custom styles for the button.
 * @param {object} props.textStyle - Custom styles for the button text.
 * @param {string} props.type - The type of the button.
 * @param {string} props.margin - The margin of the button.
 * @param {string} props.buttonHeight - The height of the button (default: 32px)
 * @param {string} props.buttonWidth - The width of the button (default: 100%)
 * @param {boolean} props.loading - The loading state of the primary button.
 * @param {object} props.icon - The icon to be rendered in the button.(react-icons)
 * @param {object} props.iconPosition - The position of the icon in the button. (left or right)
 * @returns {JSX.Element} The Primary Button component
 */
export const PrimaryButton = ({
  onClick,
  disabled,
  loading,
  title,
  style,
  textStyle,
  type,
  margin,
  buttonHeight,
  buttonWidth,
  icon,
  iconPosition,
}) => (
  <Styled.EttxPrimaryButton
    style={style}
    type={type}
    onClick={onClick}
    disabled={disabled}
    $buttonHeight={buttonHeight}
    $margin={margin}
    $buttonWidth={buttonWidth}
    {...(loading ? { loading: loading.toString() } : {})}
  >
    {loading ? (
      <ClipLoader size={22} speedMultiplier={0.4} color="#fff" />
    ) : (
      <Styled.InnerButtonContainer>
        {icon && iconPosition === 'left' && (
          <Styled.IconContainer>{icon}</Styled.IconContainer>
        )}
        <Styled.ButtonText
          disabled={disabled || loading}
          style={textStyle}
          icon={iconPosition === 'left' && icon}
        >
          {title}
        </Styled.ButtonText>
        {icon && iconPosition === 'right' && (
          <Styled.IconContainerRight>{icon}</Styled.IconContainerRight>
        )}
      </Styled.InnerButtonContainer>
    )}
  </Styled.EttxPrimaryButton>
);

/**
 * ETTX Primary Outline Button component
 *
 * @param {object} props - The props object.
 * @param {Function} props.onClick - The onClick event handler (isRequired)
 * @param {string} props.title - The title of the button (isRequired)
 * @param {boolean} props.disabled - The disabled state of the button.
 * @param {object} props.style - Custom styles for the button.
 * @param {object} props.textStyle - Custom styles for the button text.
 * @param {string} props.type - The type of the button.
 * @param {string} props.margin - The margin of the button.
 * @param {string} props.buttonHeight - The height of the button (default: 32px)
 * @param {string} props.buttonWidth - The width of the button (default: 100%)
 * @param {boolean} props.loading - The loading state of the Primary Outline button.
 * @param {object} props.icon - The icon to be rendered in the button. (react-icons)
 * @returns {JSX.Element} The Primary Outline Button component
 */
export const PrimaryOutlineButton = ({
  onClick,
  disabled,
  title,
  style,
  textStyle,
  type,
  buttonHeight,
  margin,
  buttonWidth,
  loading,
  icon,
}) => (
  <Styled.EttxPrimaryOutlineButton
    style={style}
    type={type}
    onClick={onClick}
    disabled={disabled}
    $margin={margin}
    {...(loading ? { loading: loading.toString() } : {})}
    $buttonHeight={buttonHeight}
    $buttonWidth={buttonWidth}
  >
    {loading ? (
      <ClipLoader size={22} speedMultiplier={0.4} color={theme.COLOR_BLACK} />
    ) : (
      <Styled.InnerButtonContainer>
        {icon && <Styled.IconContainer>{icon}</Styled.IconContainer>}
        <Styled.ButtonTextDark
          disabled={disabled || loading}
          style={textStyle}
          icon={icon}
        >
          {title}
        </Styled.ButtonTextDark>
      </Styled.InnerButtonContainer>
    )}
  </Styled.EttxPrimaryOutlineButton>
);

/**
 * ETTX Text Button component
 *
 * @param {object} props - The props object.
 * @param {Function} props.onClick - The onClick event handler (isRequired)
 * @param {string} props.title - The title of the button (isRequired)
 * @param {boolean} props.disabled - The disabled state of the button.
 * @param {object} props.style - Custom styles for the button.
 * @param {object} props.textStyle - Custom styles for the button text.
 * @param {string} props.type - The type of the button.
 * @param {string} props.buttonHeight - The height of the button (default: 32px)
 * @param {string} props.buttonWidth - The width of the button (default: 100%)
 * @param {boolean} props.loading - The loading state of the Primary Outline button.
 * @param {object} props.icon - The icon to be rendered in the button. (react-icons)
 * @returns {JSX.Element} The Text Button component
 */
export const TextButton = ({
  onClick,
  disabled,
  title,
  style,
  textStyle,
  type,
  buttonHeight,
  buttonWidth,
  loading,
  icon,
}) => (
  <Styled.EttxTextButton
    style={style}
    type={type}
    onClick={onClick}
    disabled={disabled}
    {...(loading ? { loading: loading.toString() } : {})}
    $buttonHeight={buttonHeight}
    $buttonWidth={buttonWidth}
    icon={icon}
  >
    {loading ? (
      <ClipLoader size={22} speedMultiplier={0.4} color="#fff" />
    ) : (
      <Styled.InnerButtonContainer>
        {icon && <Styled.IconContainer>{icon}</Styled.IconContainer>}
        <Styled.ButtonTextDark
          disabled={disabled || loading}
          style={textStyle}
          icon={icon}
        >
          {title}
        </Styled.ButtonTextDark>
      </Styled.InnerButtonContainer>
    )}
  </Styled.EttxTextButton>
);

// PROP TYPES
PrimaryButton.propTypes = {
  /** The onClick event handler. */
  onClick: propTypes.func,
  /** The disabled state of the button. */
  disabled: propTypes.bool,
  /** The title of the button. */
  title: propTypes.string.isRequired,
  /** Custom styles for the button. */
  style: propTypes.shape({}),
  /** The type of the button. */
  type: propTypes.string,
  /** Custom styles for the button text. */
  textStyle: propTypes.shape({}),
  /** The height of the button. */
  buttonHeight: propTypes.string,
  /** The margin of the button. */
  margin: propTypes.string,
  /** The width of the button. */
  buttonWidth: propTypes.string,
  /** The loading state of the button. */
  loading: propTypes.bool,
  /** The icon to be rendered in the button. */
  icon: propTypes.element,
  /** The position of the icon in the button. */
  iconPosition: propTypes.string,
};
PrimaryButton.defaultProps = {
  onClick: () => {},
  style: {},
  textStyle: {},
  type: 'button',
  disabled: false,
  margin: undefined,
  buttonWidth: 'fit-content',
  buttonHeight: '32px',
  loading: false,
  icon: null,
  iconPosition: 'left',
};

PrimaryOutlineButton.propTypes = {
  /** The onClick event handler. */
  onClick: propTypes.func,
  /** The disabled state of the button. */
  disabled: propTypes.bool,
  /** The title of the button. */
  title: propTypes.string.isRequired,
  /** Custom styles for the button. */
  style: propTypes.shape({}),
  /** The type of the button. */
  type: propTypes.string,
  /** Custom styles for the button text. */
  textStyle: propTypes.shape({}),
  /** The height of the button. */
  buttonHeight: propTypes.string,
  /** The margin of the button. */
  margin: propTypes.string,
  /** The width of the button. */
  buttonWidth: propTypes.string,
  /** The loading state of the button. */
  loading: propTypes.bool,
  /** The icon to be rendered in the button. */
  icon: propTypes.element,
};
PrimaryOutlineButton.defaultProps = {
  onClick: () => {},
  style: {},
  textStyle: {},
  type: 'button',
  disabled: false,
  margin: undefined,
  buttonWidth: 'fit-content',
  buttonHeight: '32px',
  loading: false,
  icon: null,
};

TextButton.propTypes = {
  /** The onClick event handler. */
  onClick: propTypes.func,
  /** The disabled state of the button. */
  disabled: propTypes.bool,
  /** The title of the button. */
  title: propTypes.string.isRequired,
  /** Custom styles for the button. */
  style: propTypes.shape({}),
  /** The type of the button. */
  type: propTypes.string,
  /** Custom styles for the button text. */
  textStyle: propTypes.shape({}),
  /** The height of the button. */
  buttonHeight: propTypes.string,
  /** The width of the button. */
  buttonWidth: propTypes.string,
  /** The loading state of the button. */
  loading: propTypes.bool,
  /** The icon to be rendered in the button. */
  icon: propTypes.element,
};
TextButton.defaultProps = {
  onClick: () => {},
  style: {},
  textStyle: {},
  type: 'button',
  disabled: false,
  buttonWidth: 'fit-content',
  buttonHeight: '32px',
  loading: false,
  icon: null,
};
